import { Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import ReportScheduleRedirect from './ReportScheduleRedirect'
import DashboardEditRedirect from './DashboardEditRedirect'
import NavigateKeepingQuery from './NavigateKeepingQuery'
import UuidParamGuard from './UuidParamGuard'
import ProviderRouteGuard from './ProviderRouteGuard'
import PageLoader from '@/components/common/PageLoader'
import ProtectedRoute from '@/components/auth/ProtectedRoute'
import RootLayout from '@/components/layout/RootLayout'
import AuthLayout from '@/components/layout/AuthLayout'
import NotFoundLayout from '@/components/layout/NotFoundLayout'
import { lazyWithRetry } from '@/utils/lazyWithRetry'
import { CHURCHSTAQ_SETUP_ROUTE } from '@/pages/onboarding/rock-setup/setupPath'
import { CHATGPT_CONNECT_PATH } from '@/pages/auth/authReturnTo'
import ChatGPTRouteGuard from './ChatGPTRouteGuard'

// Lazy load auth pages for code splitting
const LoginPage = lazyWithRetry(() => import('@/pages/auth/LoginPage'), 'pages/auth/LoginPage')
const SignupPage = lazyWithRetry(() => import('@/pages/auth/SignupPage'), 'pages/auth/SignupPage')
const EmailVerificationSentPage = lazyWithRetry(() => import('@/pages/auth/EmailVerificationSentPage'), 'pages/auth/EmailVerificationSentPage')
const EmailVerificationPage = lazyWithRetry(() => import('@/pages/auth/EmailVerificationPage'), 'pages/auth/EmailVerificationPage')
const PlanSelectionPage = lazyWithRetry(() => import('@/pages/auth/PlanSelectionPage'), 'pages/auth/PlanSelectionPage')
const SignupCheckoutReturnPage = lazyWithRetry(() => import('@/pages/auth/SignupCheckoutReturnPage'), 'pages/auth/SignupCheckoutReturnPage')
const OAuthCallbackPage = lazyWithRetry(() => import('@/pages/auth/OAuthCallbackPage'), 'pages/auth/OAuthCallbackPage')
const MagicLinkCallbackPage = lazyWithRetry(() => import('@/pages/auth/MagicLinkCallbackPage'), 'pages/auth/MagicLinkCallbackPage')
const PasswordResetPage = lazyWithRetry(() => import('@/pages/auth/PasswordResetPage'), 'pages/auth/PasswordResetPage')
const PasswordResetConfirmPage = lazyWithRetry(() => import('@/pages/auth/PasswordResetConfirmPage'), 'pages/auth/PasswordResetConfirmPage')
const MFAChallengePage = lazyWithRetry(() => import('@/pages/auth/MFAChallengePage'), 'pages/auth/MFAChallengePage')
const MicrosoftConnectCallbackPage = lazyWithRetry(() => import('@/pages/auth/MicrosoftConnectCallbackPage'), 'pages/auth/MicrosoftConnectCallbackPage')
const SsoLinkResumePage = lazyWithRetry(() => import('@/pages/auth/SsoLinkResumePage'), 'pages/auth/SsoLinkResumePage')
const TenantSelectPage = lazyWithRetry(() => import('@/pages/auth/TenantSelectPage'), 'pages/auth/TenantSelectPage')
const CreateOrganizationPage = lazyWithRetry(() => import('@/pages/auth/CreateOrganizationPage'), 'pages/auth/CreateOrganizationPage')
const InviteAcceptPage = lazyWithRetry(() => import('@/pages/auth/InviteAcceptPage'), 'pages/auth/InviteAcceptPage')

// Public pages (no auth required)
const SharedDashboardPage = lazyWithRetry(() => import('@/pages/dashboard/SharedDashboardPage'), 'pages/dashboard/SharedDashboardPage')
const EmbedRouter = lazyWithRetry(() => import('@/pages/embed/EmbedRouter'), 'pages/embed/EmbedRouter')
const ReportUnsubscribePage = lazyWithRetry(() => import('@/pages/public/ReportUnsubscribePage'), 'pages/public/ReportUnsubscribePage')

// Lazy load protected pages
const HomeRedirectPage = lazyWithRetry(() => import('@/pages/dashboard/HomeRedirectPage'), 'pages/dashboard/HomeRedirectPage')
const DashboardPage = lazyWithRetry(() => import('@/pages/dashboard/DashboardPage'), 'pages/dashboard/DashboardPage')
const DashboardListPage = lazyWithRetry(() => import('@/pages/dashboard/DashboardListPage'), 'pages/dashboard/DashboardListPage')
const MetricListPage = lazyWithRetry(() => import('@/pages/metrics/MetricListPage'), 'pages/metrics/MetricListPage')
// MetricCreatePage removed - using dialog-based wizard instead
const MetricDetailPage = lazyWithRetry(() => import('@/pages/metrics/MetricDetailPage'), 'pages/metrics/MetricDetailPage')
const MetricEditPage = lazyWithRetry(() => import('@/pages/metrics/MetricEditPageNew'), 'pages/metrics/MetricEditPageNew')
const CategoryListPage = lazyWithRetry(() => import('@/pages/categories/CategoryListPage'), 'pages/categories/CategoryListPage')
const CategoryDetailPage = lazyWithRetry(() => import('@/pages/categories/CategoryDetailPage'), 'pages/categories/CategoryDetailPage')
const PartitionTypeListPage = lazyWithRetry(() => import('@/pages/partitions/PartitionTypeListPage'), 'pages/partitions/PartitionTypeListPage')
const PartitionTypeDetailPage = lazyWithRetry(() => import('@/pages/partitions/PartitionTypeDetailPage'), 'pages/partitions/PartitionTypeDetailPage')
const ReportListPage = lazyWithRetry(() => import('@/pages/reports/ReportListPage'), 'pages/reports/ReportListPage')
const ReportBuilderPage = lazyWithRetry(() => import('@/pages/reports/ReportBuilderPage'), 'pages/reports/ReportBuilderPage')
const ReportViewPage = lazyWithRetry(() => import('@/pages/reports/ReportViewPage'), 'pages/reports/ReportViewPage')
const ReportDetailPage = lazyWithRetry(() => import('@/pages/reports/ReportDetailPage'), 'pages/reports/ReportDetailPage')
const ReportTemplatesPage = lazyWithRetry(() => import('@/pages/reports/ReportTemplatesPage'), 'pages/reports/ReportTemplatesPage')
const ReportTemplateEditorPage = lazyWithRetry(() => import('@/pages/reports/ReportTemplateEditorPage'), 'pages/reports/ReportTemplateEditorPage')
const IntegrationsPage = lazyWithRetry(() => import('@/pages/integrations/IntegrationsPage'), 'pages/integrations/IntegrationsPage')
const RockLinkConsentPage = lazyWithRetry(() => import('@/pages/integrations/RockLinkConsentPage'), 'pages/integrations/RockLinkConsentPage')
const RockSetupPage = lazyWithRetry(() => import('@/pages/onboarding/rock-setup/RockSetupPage'), 'pages/onboarding/rock-setup/RockSetupPage')
const PcoSetupPage = lazyWithRetry(() => import('@/pages/onboarding/pco-setup/PcoSetupPage'), 'pages/onboarding/pco-setup/PcoSetupPage')
const ChurchStaqSetupPage = lazyWithRetry(() => import('@/pages/onboarding/churchstaq-setup/ChurchStaqSetupPage'), 'pages/onboarding/churchstaq-setup/ChurchStaqSetupPage')
const MeasurementsBoardPage = lazyWithRetry(() => import('@/pages/integrations/measurements/MeasurementsBoardPage'), 'pages/integrations/measurements/MeasurementsBoardPage')
const MappingsPage = lazyWithRetry(() => import('@/pages/integrations/measurements/mappings/MappingsPage'), 'pages/integrations/measurements/mappings/MappingsPage')
const SuggestionsPage = lazyWithRetry(() => import('@/pages/integrations/suggestions/SuggestionsPage'), 'pages/integrations/suggestions/SuggestionsPage')
const FormListPage = lazyWithRetry(() => import('@/pages/forms/FormListPage'), 'pages/forms/FormListPage')
const FormBuilderPage = lazyWithRetry(() => import('@/pages/forms/FormBuilderPage'), 'pages/forms/FormBuilderPage')
const FormFillPage = lazyWithRetry(() => import('@/pages/forms/FormFillPage'), 'pages/forms/FormFillPage')
const GettingStartedPage = lazyWithRetry(() => import('@/pages/onboarding/GettingStartedPage'), 'pages/onboarding/GettingStartedPage')
const SettingsPage = lazyWithRetry(() => import('@/pages/settings/SettingsPage'), 'pages/settings/SettingsPage')
const SettingsHome = lazyWithRetry(() => import('@/pages/settings/SettingsHome'), 'pages/settings/SettingsHome')
const BrandingSettingsPage = lazyWithRetry(() => import('@/pages/settings/BrandingSettingsPage'), 'pages/settings/BrandingSettingsPage')
const OrganizationSettingsPage = lazyWithRetry(() => import('@/pages/settings/OrganizationSettingsPage'), 'pages/settings/OrganizationSettingsPage')
const UsersSettingsPage = lazyWithRetry(() => import('@/pages/settings/UsersSettingsPage'), 'pages/settings/UsersSettingsPage')
const CampusesSettingsPage = lazyWithRetry(() => import('@/pages/settings/CampusesSettingsPage'), 'pages/settings/CampusesSettingsPage')
const AuthenticationSettingsPage = lazyWithRetry(() => import('@/pages/settings/AuthenticationSettingsPage'), 'pages/settings/AuthenticationSettingsPage')
const SubscriptionManagement = lazyWithRetry(() => import('@/pages/settings/SubscriptionManagement'), 'pages/settings/SubscriptionManagement')
const ChartColorsSettingsPage = lazyWithRetry(() => import('@/pages/settings/ChartColorsSettingsPage'), 'pages/settings/ChartColorsSettingsPage')
const AiSettingsPage = lazyWithRetry(() => import('@/pages/settings/AiSettingsPage'), 'pages/settings/AiSettingsPage')
const AnnotationsSettingsPage = lazyWithRetry(() => import('@/pages/settings/AnnotationsSettingsPage'), 'pages/settings/AnnotationsSettingsPage')
const TrialExpiredPage = lazyWithRetry(() => import('@/pages/subscription/TrialExpiredPage'), 'pages/subscription/TrialExpiredPage')
const ProfilePage = lazyWithRetry(() => import('@/pages/ProfilePage'), 'pages/ProfilePage')
const BriefPage = lazyWithRetry(() => import('@/pages/ai/BriefPage'), 'pages/ai/BriefPage')

// 404 page
const NotFoundPage = lazyWithRetry(() => import('@/pages/NotFoundPage'), 'pages/NotFoundPage')
const ChatGPTConnectPage = lazyWithRetry(() => import('@/pages/integrations/ChatGPTConnectPage'), 'pages/integrations/ChatGPTConnectPage')

// Living style guide. Public and DEV-only unless explicitly enabled, so the
// Playwright screenshots need no login fixture and production stays clean.
const DesignSystemPage = lazyWithRetry(() => import('@/pages/DesignSystemPage'), 'pages/DesignSystemPage')
const isDesignSystemEnabled =
  import.meta.env.DEV || import.meta.env.VITE_ENABLE_DESIGN_SYSTEM === 'true'

/**
 * Application routes configuration with nested layouts and lazy loading
 *
 * Route hierarchy:
 * - AuthLayout: Public routes (login, signup, OAuth callbacks, password reset, tenant select)
 * - ProtectedRoute + RootLayout: Protected routes (dashboard, metrics, categories, reports, settings, profile)
 * Render errors are caught by AppErrorBoundary (mounted in App.jsx and
 * RootLayout.jsx), not by react-router: `errorElement` only works with data
 * routers, and this app uses the component <Routes> API.
 *
 * @returns {JSX.Element}
 */
export default function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Root: send the user to their default dashboard, if they've set one */}
        <Route path="/" element={<ProtectedRoute><HomeRedirectPage /></ProtectedRoute>} />

        {/* Public routes with AuthLayout */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/verify-email-sent" element={<EmailVerificationSentPage />} />
          <Route path="/verify-email" element={<EmailVerificationPage />} />
          <Route path="/auth/callback/google" element={<OAuthCallbackPage />} />
          <Route path="/auth/callback/github" element={<OAuthCallbackPage />} />
          <Route path="/auth/callback/microsoft" element={<OAuthCallbackPage />} />
          <Route path="/auth/magic-link/:token" element={<MagicLinkCallbackPage />} />
          <Route path="/password-reset" element={<PasswordResetPage />} />
          {/* The emailed link points here (authentication/utils.py builds
              FRONTEND_URL + "/reset-password?token="), so the path has to
              match that string exactly. */}
          <Route path="/reset-password" element={<PasswordResetConfirmPage />} />
          <Route path="/auth/mfa-challenge" element={<MFAChallengePage />} />
        </Route>

        {/* Public pages (no auth, no layout) */}
        <Route path="/invite/:token" element={<InviteAcceptPage />} />
        {/* Kept as an alias for already-deployed Rock blocks' stored embed
            URLs (EG-2); /embed/dashboard/:token is the same page. */}
        <Route path="/shared/:shareToken" element={<SharedDashboardPage />} />
        <Route path="/embed/:resourceType/:token" element={<EmbedRouter />} />
        {/* Unsubscribe link in scheduled report emails (backend
            core.services.report_unsubscribe builds FRONTEND_URL + this path). */}
        <Route path="/unsubscribe/:token" element={<ReportUnsubscribePage />} />

        {/* Retired: OnboardingPage + OnboardingWizard merged into the single
            "Getting started" checklist (/getting-started). Any deep link
            into the old flow, with or without a sub-path, lands there. */}
        <Route path="/onboarding/*" element={<Navigate to="/getting-started" replace />} />

        {/* Pay-during-signup plan selection + Stripe checkout return (auth, no layout) */}
        <Route path="/signup/select-plan" element={<ProtectedRoute><PlanSelectionPage /></ProtectedRoute>} />
        <Route path="/signup/checkout-return" element={<ProtectedRoute><SignupCheckoutReturnPage /></ProtectedRoute>} />

        {/* Tenant selection page (requires auth but not layout) */}
        <Route path="/tenant-select" element={<ProtectedRoute><TenantSelectPage /></ProtectedRoute>} />

        {/* Signed-in user with no organization naming a new one (auth, no layout) */}
        <Route path="/organizations/new" element={<ProtectedRoute><CreateOrganizationPage /></ProtectedRoute>} />

        {/* Trial expired page (requires auth but not layout - hard paywall) */}
        <Route path="/trial-expired" element={<ProtectedRoute><TrialExpiredPage /></ProtectedRoute>} />

        {/* One-click Microsoft sign-in's connect flow returns here. The page
            protects only the finishing step (no layout): the admin-consent
            return may be opened by someone with no Graphit account. */}
        <Route path="/auth/callback/microsoft-connect" element={<MicrosoftConnectCallbackPage />} />

        {/* Connect Google/Microsoft after a 403 link_required sign-in
            (requires auth but not layout): the sign-in returns here. */}
        <Route path="/auth/link/:provider" element={<ProtectedRoute><SsoLinkResumePage /></ProtectedRoute>} />

        {/* Rock-initiated OAuth consent (requires auth but not layout - standalone page) */}
        <Route path="/integrations/link" element={<ProtectedRoute><RockLinkConsentPage /></ProtectedRoute>} />
        {/* ChatGPT account linking is flagged (/me.chatgpt_enabled, off by default): 404 while off. */}
        <Route
          path={CHATGPT_CONNECT_PATH}
          element={<ChatGPTRouteGuard element={<ChatGPTConnectPage />} fallback={<NotFoundPage />} />}
        />

        {/* Guided Rock setup (ROCK-ONBOARDING-REWORK T4): full screen, outside
            the app shell and Settings. Owners/admins only (the page guards). */}
        <Route path="/setup/rock/:connectionId" element={<ProtectedRoute><RockSetupPage /></ProtectedRoute>} />
        <Route path="/setup/planning-center/:connectionId" element={<ProtectedRoute><PcoSetupPage /></ProtectedRoute>} />
        {/* ChurchStaq is a flagged provider: its setup 404s for a tenant whose catalog omits it. */}
        <Route
          path={CHURCHSTAQ_SETUP_ROUTE}
          element={(
            <ProtectedRoute>
              <ProviderRouteGuard providerKey="churchstaq" element={<ChurchStaqSetupPage />} fallback={<NotFoundPage />} />
            </ProtectedRoute>
          )}
        />

        {/* Protected routes with ProtectedRoute wrapper and RootLayout */}
        <Route element={<ProtectedRoute><RootLayout /></ProtectedRoute>}>
          <Route path="/dashboards" element={<DashboardListPage />} />
          <Route path="/dashboards/:dashboardId" element={<DashboardPage />} />
          {/* Legacy standalone edit page: now the dashboard, in edit mode, settings open */}
          <Route path="/dashboards/:dashboardId/edit" element={<DashboardEditRedirect />} />

          {/* Getting started checklist — the one first-run surface (merges
              the old /onboarding page and the dashboards-page wizard). */}
          <Route path="/getting-started" element={<GettingStartedPage />} />

          {/* Metrics routes */}
          <Route path="/metrics" element={<MetricListPage />} />
          <Route path="/metrics/new" element={<Navigate to="/metrics" replace />} />
          {/* Categories sit under Metrics in the sidebar, so people try this path */}
          <Route path="/metrics/categories" element={<Navigate to="/categories" replace />} />
          <Route path="/metrics/:id" element={<UuidParamGuard param="id" element={<MetricDetailPage />} fallback={<NotFoundPage />} />} />
          <Route path="/metrics/:id/edit" element={<UuidParamGuard param="id" element={<MetricEditPage />} fallback={<NotFoundPage />} />} />

          {/* Data Entry (Forms) routes */}
          <Route path="/data-entry" element={<FormListPage />} />
          <Route path="/data-entry/new" element={<FormBuilderPage />} />
          <Route path="/data-entry/:id/edit" element={<FormBuilderPage />} />
          <Route path="/data-entry/:id/fill" element={<FormFillPage />} />

          {/* Legacy forms routes - redirect to data-entry */}
          <Route path="/forms" element={<Navigate to="/data-entry" replace />} />
          <Route path="/forms/*" element={<Navigate to="/data-entry" replace />} />

          {/* Categories routes */}
          <Route path="/categories" element={<CategoryListPage />} />
          <Route path="/categories/:id" element={<CategoryDetailPage />} />

          {/* Partition types routes */}
          <Route path="/partitions" element={<PartitionTypeListPage />} />
          <Route path="/partitions/:id" element={<PartitionTypeDetailPage />} />

          {/* Reports routes */}
          <Route path="/reports" element={<ReportListPage />} />
          <Route path="/reports/new" element={<ReportBuilderPage />} />
          <Route path="/reports/:id/edit" element={<ReportBuilderPage />} />
          <Route path="/reports/:id/view" element={<ReportViewPage />} />
          <Route path="/reports/:reportId/schedule" element={<ReportScheduleRedirect />} />
          <Route path="/reports/:reportId" element={<ReportDetailPage />} />

          {/* Report Templates (PDF) routes */}
          <Route path="/report-templates" element={<ReportTemplatesPage />} />
          <Route path="/report-templates/new" element={<ReportTemplateEditorPage />} />
          <Route path="/report-templates/:id/edit" element={<ReportTemplateEditorPage />} />

          {/* Redirect old integrations route to its settings section */}
          <Route path="/integrations" element={<Navigate to="/settings/integrations" replace />} />
          <Route path="/integrations/suggestions" element={<NavigateKeepingQuery to="/settings/integrations/suggestions" />} />

          {/* Settings home and section pages */}
          <Route path="/settings" element={<SettingsPage />}>
            <Route index element={<SettingsHome />} />
            <Route path="organization" element={<OrganizationSettingsPage />} />
            <Route path="branding" element={<BrandingSettingsPage />} />
            <Route path="users" element={<UsersSettingsPage />} />
            <Route path="campuses" element={<CampusesSettingsPage />} />
            {/* Same component as /categories (CategoryListPage) — see finding E,
                2026-09-19 audit: two diverging UIs over the same resource. */}
            <Route path="categories" element={<CategoryListPage embedded />} />
            <Route path="partitions" element={<PartitionTypeListPage embedded />} />
            <Route path="chart-colors" element={<ChartColorsSettingsPage />} />
            <Route path="authentication" element={<AuthenticationSettingsPage />} />
            <Route path="integrations" element={<IntegrationsPage />} />
            <Route path="integrations/rock/:connectionId/measurements" element={<MeasurementsBoardPage />} />
            <Route path="integrations/rock/:connectionId/measurements/mappings" element={<MappingsPage />} />
            <Route path="integrations/suggestions" element={<SuggestionsPage />} />
            <Route path="ai" element={<AiSettingsPage />} />
            <Route path="annotations" element={<AnnotationsSettingsPage />} />
            <Route path="subscription" element={<SubscriptionManagement />} />
          </Route>

          {/* Profile route */}
          <Route path="/profile" element={<ProfilePage />} />

          {/* Weekly AI brief viewer (B3) */}
          <Route path="/ai/briefs/:id" element={<BriefPage />} />
        </Route>

        {/* Living style guide — no auth, no layout; see isDesignSystemEnabled */}
        {isDesignSystemEnabled && (
          <Route path="/design-system" element={<DesignSystemPage />} />
        )}

        {/* 404 catch-all: inside the app shell when signed in (UI audit S8) */}
        <Route element={<NotFoundLayout />}>
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  )
}
